//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';

import ActionButton from '@components/atoms/action-button/ActionButton';

interface Props {
  containerItem: React.ReactElement;
  nestedItems: React.ReactElement[];
  actionProp: 'linkAction' | 'headerAction';
  useNestedItemStyles?: boolean;
  initExpanded: boolean;
}

type SidebarItemGroupType = Pick<
  Props,
  'containerItem' | 'nestedItems' | 'initExpanded' | 'useNestedItemStyles'
>;

const SidebarItemGroup = ({
  actionProp,
  containerItem,
  initExpanded = false,
  nestedItems,
  useNestedItemStyles = true,
}: Props) => {
  const [nestedItemsOpen, setNestedItemsOpen] = useState(initExpanded);
  const hasNestedItems = nestedItems.length >= 1;

  const toggleNestedItemsOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setNestedItemsOpen(!nestedItemsOpen);
  };

  const dropdownButton = hasNestedItems && (
    <ActionButton
      iconName={nestedItemsOpen ? 'chevron-down' : 'chevron-right'}
      onClick={toggleNestedItemsOpen}
    />
  );

  const wrappedContainerItem = React.cloneElement(containerItem, {
    [actionProp]: dropdownButton,
    // Clicking on the item should set the nested items open
    onClick: () => setNestedItemsOpen(true),
  });

  const wrappedNestedItems = nestedItems.map((nestedItem) =>
    React.cloneElement(nestedItem, {
      isNested: useNestedItemStyles,
    }),
  );

  return (
    <>
      {wrappedContainerItem}
      {nestedItemsOpen && wrappedNestedItems}
    </>
  );
};

export const SidebarPageItemGroup = ({
  containerItem,
  initExpanded,
  nestedItems,
  useNestedItemStyles = true,
}: SidebarItemGroupType) => {
  return (
    <SidebarItemGroup
      actionProp='linkAction'
      containerItem={containerItem}
      initExpanded={initExpanded}
      nestedItems={nestedItems}
      useNestedItemStyles={useNestedItemStyles}
    />
  );
};
